import React from "react";
import SkeletonLoader from "components/ui/SkeletonLoader";
import './CompanyProfileSkeleton.css';

/**
 * Skeleton loader that mirrors the CompanyProfileView layout:
 *  - Hero card  (banner + logo + name + stats)
 *  - Tab bar
 *  - Overview body  (About card + Perks sidebar)
 *  - Two more content cards
 */
const CompanyProfileSkeleton: React.FC = () => (
  <div className="cpv-page">
    <div className="cpv-shell" style={{ padding: "0 16px 24px" }}>

      {/* ── Hero card ── */}
      <div
        className="cpv-hero-card"
        style={{ background: "#fff", borderRadius: 16, overflow: "hidden", border: "1px solid #2929291A" }}
      >
        {/* Banner */}
        <SkeletonLoader height={140} width="100%" />

        {/* Hero body */}
        <div style={{ padding: "0 24px 20px", marginTop: -36 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 18 }}>
            {/* Logo + name block */}
            <div style={{ display: "flex", gap: 16, alignItems: "flex-end" }}>
              {/* Logo */}
              <SkeletonLoader width={88} height={88} style={{ borderRadius: 14, flexShrink: 0 }} />

              <div style={{ paddingTop: 40, display: "flex", flexDirection: "column", gap: 8 }}>
                {/* Company name */}
                <SkeletonLoader width={220} height={24} />
                {/* Tagline */}
                <SkeletonLoader width={300} height={16} />
                {/* Meta row: employees · founded · location · badge */}
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
                  <SkeletonLoader width={60} height={14} />
                  <SkeletonLoader width={110} height={14} />
                  <SkeletonLoader width={140} height={14} />
                  <SkeletonLoader width={70} height={14} />
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: "flex", gap: 8, paddingTop: 48 }}>
              <SkeletonLoader width={80} height={34} style={{ borderRadius: 8 }} />
              <SkeletonLoader width={90} height={34} style={{ borderRadius: 8 }} />
            </div>
          </div>

          {/* Stats row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 12,
              marginTop: 20,
            }}
          >
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  borderRadius: 12,
                  padding: "14px 16px",
                  background: "#f6f8fd",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <SkeletonLoader width={32} height={32} circle />
                <SkeletonLoader width="60%" height={22} />
                <SkeletonLoader width="80%" height={14} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          border: "1px solid #2929291A",
          padding: "0 24px",
          display: "flex",
          gap: 24,
          alignItems: "center",
          height: 52,
        }}
      >
        {[100, 90, 80, 100, 90].map((w, i) => (
          <SkeletonLoader key={i} width={w} height={16} />
        ))}
      </div>

      {/* ── Overview body: main column + sidebar ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 16, alignItems: "start" }}>

        {/* Left column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* About company card */}
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              border: "1px solid #2929291A",
              padding: 24,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <SkeletonLoader width={160} height={20} />
                <SkeletonLoader width={220} height={14} />
              </div>
              <SkeletonLoader width={50} height={28} style={{ borderRadius: 8 }} />
            </div>
            <SkeletonLoader width={80} height={12} style={{ marginBottom: 8 }} />
            <SkeletonLoader count={3} height={16} width="100%" />
            <SkeletonLoader width="70%" height={16} />
          </div>

          {/* Core Expertise card */}
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              border: "1px solid #2929291A",
              padding: 24,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <SkeletonLoader width={140} height={20} />
                <SkeletonLoader width={200} height={14} />
              </div>
              <SkeletonLoader width={50} height={28} style={{ borderRadius: 8 }} />
            </div>
            <SkeletonLoader width={80} height={12} style={{ marginBottom: 10 }} />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {[90, 130, 100, 110, 80, 120, 95, 105].map((w, i) => (
                <SkeletonLoader key={i} width={w} height={28} style={{ borderRadius: 20 }} />
              ))}
            </div>
          </div>

          {/* Open Roles card */}
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              border: "1px solid #2929291A",
              padding: 24,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <SkeletonLoader width={120} height={20} />
              <SkeletonLoader width={80} height={28} style={{ borderRadius: 8 }} />
            </div>
            {[1, 2].map((i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 0",
                  borderBottom: i < 2 ? "1px solid #f0f0f0" : "none",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <SkeletonLoader width={180} height={16} />
                  <SkeletonLoader width={130} height={13} />
                </div>
                <SkeletonLoader width={70} height={28} style={{ borderRadius: 8 }} />
              </div>
            ))}
          </div>
        </div>

        {/* Right sidebar */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Perks & Benefits card */}
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              border: "1px solid #2929291A",
              padding: 24,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <SkeletonLoader width={140} height={20} />
                <SkeletonLoader width={100} height={14} />
              </div>
              <SkeletonLoader width={50} height={28} style={{ borderRadius: 8 }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  style={{
                    background: "#f6f8fd",
                    borderRadius: 12,
                    padding: 14,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <SkeletonLoader width={28} height={28} circle />
                  <SkeletonLoader width="80%" height={14} />
                  <SkeletonLoader width="100%" height={12} />
                  <SkeletonLoader width="60%" height={12} />
                </div>
              ))}
            </div>
          </div>

          {/* Contact / Info card */}
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              border: "1px solid #2929291A",
              padding: 24,
            }}
          >
            <SkeletonLoader width={120} height={18} style={{ marginBottom: 16 }} />
            {[1, 2, 3, 4].map((i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
                <SkeletonLoader width={18} height={18} circle />
                <SkeletonLoader width="75%" height={14} />
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  </div>
);

export default CompanyProfileSkeleton;
